import $ from 'jquery';
import TomSelect from 'tom-select';

const scoreByMatchingString = (search) => (item) => {
  const term = search.replace(/\s+/g, '').toLowerCase();
  const text = item.text.replace(/\s+/g, '').toLowerCase();
  return text.includes(term) ? 1 : 0;
};

const alreadyTomselected = (element) => {
  if (element.classList.contains('tomselected')) {
    console.debug('Element was already tomselected.');
    console.debug(element);

    return true;
  }

  return false;
};

const updateTomSelectState = (element) => {
  if (element.hasAttribute('disabled') && !element.tomselect.isDisabled) {
    element.tomselect.disable();
  } else if (!element.hasAttribute('disabled') && element.tomselect.isDisabled) {
    element.tomselect.enable();
  }
};

const observeTomSelectDisabledAttribute = (element) => {
  const observer = new MutationObserver(() => updateTomSelectState(element));
  observer.observe(element, { attributes: true, attributeFilter: ['disabled'] });
};

const setupTomSelect = (element) => {
  if (alreadyTomselected(element)) {
    return null;
  }

  const tomSelectOptions = {
    maxOptions: null,
    score: scoreByMatchingString
  };

  if (element.dataset.maxItems) {
    tomSelectOptions.maxItems = parseInt(element.dataset.maxItems, 10);
  }

  const tomSelect = new TomSelect(element, tomSelectOptions);

  updateTomSelectState(element);
  observeTomSelectDisabledAttribute(element);

  return tomSelect;
};

const setupTomSelectTagging = (element) => {
  if (alreadyTomselected(element)) {
    return null;
  }

  const tomSelect = new TomSelect(element, {
    plugins: {
      remove_button: {}
    },
    create: true,
    delimiter: ',',
    hidePlaceholder: true,
    maxOptions: null,
    maxItems: null,
    scoreByMatchingString
  });

  updateTomSelectState(element);
  observeTomSelectDisabledAttribute(element);

  return tomSelect;
};

const setupTomSelectAjax = (element, url) => {
  const tomSelect = new TomSelect(element, {
    maxOptions: null,
    valueField: 'value',
    labelField: 'label',
    searchField: 'label',
    load(query, callback) {
      const headers = new Headers({
	'Content-Type': 'application/json',
	'X-Api-Version': '1'
      });

      const queryURL = `${url}?term=${encodeURIComponent(query)}`;

      fetch(queryURL, { headers })
	.then(response => response.json())
	.then(json => {
	  callback(json.data);
	}).catch((error) => {
	  console.debug(error);

	  callback();
	});
    }
  });

  updateTomSelectState(element);
  observeTomSelectDisabledAttribute(element);

  return tomSelect;
};

const fareAdapterElementFieldSelector = (attribute, tag = 'select') => [
  `${tag}[name*="[elements_attributes]"][name*="[${attribute}]"]`,
  `${tag}[name="fare_adapter_configuration_element[${attribute}]"]`
].join(', ');

const fareAdapterElementPriceTypeSelector = fareAdapterElementFieldSelector('price_type');
const fareAdapterElementDiscountTypeSelector = fareAdapterElementFieldSelector('discount_type');
const fareAdapterElementAbsoluteAmountSelector = fareAdapterElementFieldSelector('absolute_amount', 'input');
const fareAdapterElementBasePriceMappingSelector = fareAdapterElementFieldSelector('base_price_mapping', 'textarea');
const fareAdapterElementBasePriceMappingHintSelector = '.js-fare-adapter-element-base-price-mapping-hint';
const fareAdapterElementRelativeAmountSelector = fareAdapterElementFieldSelector('relative_amount', 'input');
const fareAdapterElementTargetAmountSelector = fareAdapterElementFieldSelector('target_amount', 'input');
const fareAdapterElementPassengerOptionSelector = fareAdapterElementFieldSelector('passenger_option_id');
const fareAdapterElementRideOptionSelector = fareAdapterElementFieldSelector('ride_option_id');
const fareAdapterElementPassengerTypesSelector = 'select.js-fare-adapter-element-passenger-types';
const fareAdapterElementFreeCompanionWrapperSelector = '.js-fare-adapter-element-free-companion';
const fareAdapterElementCompanionPassengerTypesWrapperSelector = '.js-fare-adapter-element-companion-passenger-types';
const fareAdapterElementGrantsFreeCompanionSelector = [
  'input[type="checkbox"][name*="[elements_attributes]"][name*="[grants_free_companion]"]',
  'input[type="checkbox"][name="fare_adapter_configuration_element[grants_free_companion]"]'
].join(', ');

const prefillFareAdapterElementBasePriceMapping = (basePriceMappingField) => {
  if (!basePriceMappingField) return;
  if (basePriceMappingField.value.trim() !== '') return;
  if (!basePriceMappingField.dataset.defaultValue) return;

  basePriceMappingField.value = basePriceMappingField.dataset.defaultValue;
};

const syncFareAdapterElementPassengerTypesState = (container, priceTypeField) => {
  const passengerTypesField = container.querySelector(fareAdapterElementPassengerTypesSelector);
  if (!passengerTypesField) return;

  const isRideOptionSurcharge = priceTypeField.value === 'ride_option_surcharge';

  if (isRideOptionSurcharge) {
    passengerTypesField.disabled = true;
    if (passengerTypesField.tomselect) {
      passengerTypesField.tomselect.clear(true);
    }
    return;
  }

  passengerTypesField.disabled = false;

  const isSingleSelect = priceTypeField.value === 'base_price';
  if (isSingleSelect) {
    passengerTypesField.dataset.maxItems = '1';
  } else {
    delete passengerTypesField.dataset.maxItems;
  }

  if (!passengerTypesField.tomselect) return;

  const tomSelect = passengerTypesField.tomselect;

  if (isSingleSelect && tomSelect.items.length > 1) {
    tomSelect.setValue(tomSelect.items[0], true);
  }

  tomSelect.settings.mode = isSingleSelect ? 'single' : 'multi';
  tomSelect.setMaxItems(isSingleSelect ? 1 : null);
  tomSelect.refreshOptions(false);
};

// The eligible companion passenger types only matter once a free companion is granted.
const syncFareAdapterElementCompanionPassengerTypesState = (container, visible) => {
  const wrapper = container.querySelector(fareAdapterElementCompanionPassengerTypesWrapperSelector);
  if (!wrapper) return;

  wrapper.hidden = !visible;
  if (visible) return;

  const select = wrapper.querySelector('select');
  if (!select) return;

  if (select.tomselect) {
    select.tomselect.clear(true);
  } else {
    select.selectedIndex = -1;
  }
};

// A free companion only applies to a discount tied to a whitelisted passenger option
// (the whitelist is rendered onto the wrapper as `data-option-types`).
const syncFareAdapterElementFreeCompanionState = (container, priceTypeField) => {
  const wrapper = container.querySelector(fareAdapterElementFreeCompanionWrapperSelector);
  if (!wrapper) return;

  const passengerOptionField = container.querySelector(fareAdapterElementPassengerOptionSelector);
  const selectedOptionType = passengerOptionField?.selectedOptions?.[0]?.dataset?.optionType;
  const whitelistedOptionTypes = (wrapper.dataset.optionTypes || '').split(' ').filter(Boolean);

  const visible = priceTypeField.value === 'discount' && whitelistedOptionTypes.includes(selectedOptionType);
  wrapper.hidden = !visible;

  const checkbox = container.querySelector(fareAdapterElementGrantsFreeCompanionSelector);
  if (!visible && checkbox) checkbox.checked = false;

  syncFareAdapterElementCompanionPassengerTypesState(container, visible && !!checkbox?.checked);
};

const syncFareAdapterElementState = (container) => {
  const priceTypeField = container.querySelector(fareAdapterElementPriceTypeSelector);
  if (!priceTypeField) return;

  const isRideOptionSurcharge = priceTypeField.value === 'ride_option_surcharge';
  const disablesDiscountType = ['base_price', 'zone_code_base_price_mapping', 'surcharge', 'ride_option_surcharge'].includes(priceTypeField.value);
  const discountTypeField = container.querySelector(fareAdapterElementDiscountTypeSelector);
  const discountType = disablesDiscountType ? null : discountTypeField?.value;
  const absoluteAmountField = container.querySelector(fareAdapterElementAbsoluteAmountSelector);
  const basePriceMappingField = container.querySelector(fareAdapterElementBasePriceMappingSelector);
  const basePriceMappingHint = container.querySelector(fareAdapterElementBasePriceMappingHintSelector);
  const relativeAmountField = container.querySelector(fareAdapterElementRelativeAmountSelector);
  const targetAmountField = container.querySelector(fareAdapterElementTargetAmountSelector);
  const passengerOptionField = container.querySelector(fareAdapterElementPassengerOptionSelector);
  const rideOptionField = container.querySelector(fareAdapterElementRideOptionSelector);

  if (discountTypeField) {
    discountTypeField.disabled = disablesDiscountType;
  }

  if (absoluteAmountField) {
    absoluteAmountField.disabled = priceTypeField.value === 'zone_code_base_price_mapping' || ['relative', 'target_price'].includes(discountType);
  }

  if (basePriceMappingField) {
    if (priceTypeField.value === 'zone_code_base_price_mapping') {
      prefillFareAdapterElementBasePriceMapping(basePriceMappingField);
    }

    basePriceMappingField.disabled = priceTypeField.value !== 'zone_code_base_price_mapping';
  }

  if (basePriceMappingHint) {
    basePriceMappingHint.hidden = priceTypeField.value !== 'zone_code_base_price_mapping';
  }

  if (relativeAmountField) {
    relativeAmountField.disabled = disablesDiscountType || ['absolute', 'target_price'].includes(discountType);
  }

  if (targetAmountField) {
    targetAmountField.disabled = isRideOptionSurcharge || discountType !== 'target_price';
  }

  if (passengerOptionField) {
    passengerOptionField.disabled = isRideOptionSurcharge;
    if (isRideOptionSurcharge && passengerOptionField.tomselect) {
      passengerOptionField.tomselect.clear(true);
    }
  }

  if (rideOptionField) {
    rideOptionField.disabled = !isRideOptionSurcharge;
    if (!isRideOptionSurcharge && rideOptionField.tomselect) {
      rideOptionField.tomselect.clear(true);
    }
  }

  syncFareAdapterElementFreeCompanionState(container, priceTypeField);
  syncFareAdapterElementPassengerTypesState(container, priceTypeField);
};

const fareAdapterElementContainer = (element) => element.closest('tr') || element.closest('.js-fare-adapter-configuration-element-form');

const tomSelectSelector = 'select:not([data-no-tomselect]):not(.js-autocomplete-user-widget):not(.js-autocomplete-station-widget):not(.js-tagging)';
const fareAdapterElementEventNamespace = '.fareAdapterElement';

const bindFareAdapterElementFormEvents = (container) => {
  if (container.getAttribute('data-fare-adapter-element-events-bound') === 'true') return;

  container.addEventListener('change', (event) => {
    if (event.target.matches(fareAdapterElementPriceTypeSelector)) {
      const targetContainer = fareAdapterElementContainer(event.target);
      if (!targetContainer) return;
      const basePriceMappingField = targetContainer.querySelector(fareAdapterElementBasePriceMappingSelector);

      if (['base_price', 'zone_code_base_price_mapping', 'surcharge', 'ride_option_surcharge'].includes(event.target.value)) {
        const discountTypeField = targetContainer.querySelector(fareAdapterElementDiscountTypeSelector);
        const relativeAmountField = targetContainer.querySelector(fareAdapterElementRelativeAmountSelector);
        const targetAmountField = targetContainer.querySelector(fareAdapterElementTargetAmountSelector);

        if (discountTypeField) {
          discountTypeField.value = '';
          if (discountTypeField.tomselect) {
            discountTypeField.tomselect.clear(true);
          }
        }

        if (relativeAmountField) {
          relativeAmountField.value = '';
        }

        if (targetAmountField) {
          targetAmountField.value = '';
        }
      }

      if (event.target.value === 'ride_option_surcharge') {
        const passengerOptionField = targetContainer.querySelector(fareAdapterElementPassengerOptionSelector);
        if (passengerOptionField) {
          passengerOptionField.value = '';
          if (passengerOptionField.tomselect) {
            passengerOptionField.tomselect.clear(true);
          }
        }
      }

      if (event.target.value !== 'ride_option_surcharge') {
        const rideOptionField = targetContainer.querySelector(fareAdapterElementRideOptionSelector);
        if (rideOptionField) {
          rideOptionField.value = '';
          if (rideOptionField.tomselect) {
            rideOptionField.tomselect.clear(true);
          }
        }
      }

      if (basePriceMappingField && event.target.value !== 'zone_code_base_price_mapping') {
        basePriceMappingField.value = '';
      }

      syncFareAdapterElementState(targetContainer);
      return;
    }

    if (event.target.matches(fareAdapterElementDiscountTypeSelector)) {
      const targetContainer = fareAdapterElementContainer(event.target);
      if (!targetContainer) return;

      const absoluteAmountField = targetContainer.querySelector(fareAdapterElementAbsoluteAmountSelector);
      const relativeAmountField = targetContainer.querySelector(fareAdapterElementRelativeAmountSelector);
      const targetAmountField = targetContainer.querySelector(fareAdapterElementTargetAmountSelector);

      // Only one of absolute_amount / relative_amount / target_amount applies per discount type.
      if (['relative', 'target_price'].includes(event.target.value) && absoluteAmountField) {
        absoluteAmountField.value = '';
      }

      if (['absolute', 'target_price'].includes(event.target.value) && relativeAmountField) {
        relativeAmountField.value = '';
      }

      if (event.target.value !== 'target_price' && targetAmountField) {
        targetAmountField.value = '';
      }

      syncFareAdapterElementState(targetContainer);
      return;
    }

    if (event.target.matches(fareAdapterElementPassengerOptionSelector)) {
      const targetContainer = fareAdapterElementContainer(event.target);
      if (!targetContainer) return;

      syncFareAdapterElementState(targetContainer);
      return;
    }

    if (event.target.matches(fareAdapterElementGrantsFreeCompanionSelector)) {
      const targetContainer = fareAdapterElementContainer(event.target);
      if (!targetContainer) return;

      syncFareAdapterElementState(targetContainer);
    }
  });

  container.setAttribute('data-fare-adapter-element-events-bound', 'true');
};

const initializeFareAdapterElementForm = (container) => {
  container
    .querySelectorAll(tomSelectSelector)
    .forEach((element) => setupTomSelect(element));

  bindFareAdapterElementFormEvents(container);
  syncFareAdapterElementState(container);
};

const setupFareAdapterElementForm = () => {
  $(document).ready(() => {
    document
      .querySelectorAll('tr')
      .forEach((row) => syncFareAdapterElementState(row));

    document
      .querySelectorAll('.js-fare-adapter-configuration-element-form')
      .forEach((form) => initializeFareAdapterElementForm(form));

    $(document)
      .off(`click${fareAdapterElementEventNamespace}`, '.js-add-row-for-fare-adapter-element')
      .on('click', '.js-add-row-for-fare-adapter-element', (event) => {
      const button = event.currentTarget;
      const tableId = button.dataset.table;
      const table = document.getElementById(tableId);
      if (!table) return;
      const tableBody = table.querySelector('tbody');

      const time = Date.now();
      const regexp = new RegExp(button.dataset.id, 'g');
      const newRowHTML = button.dataset.fields.replace(regexp, time);
      const newRow = document.createElement('tr');
      newRow.innerHTML = newRowHTML;

      newRow
        .querySelectorAll('select:not([data-no-tomselect]):not(.js-autocomplete-user-widget):not(.js-autocomplete-station-widget):not(.js-tagging)')
        .forEach((element) => setupTomSelect(element));

      syncFareAdapterElementState(newRow);

      if (tableBody) {
        tableBody.insertAdjacentElement('beforeend', newRow);
      } else {
        table.insertAdjacentElement('beforeend', newRow);
      }

      if (button.dataset.localizedFields && button.dataset.localizedContainer) {
        const localizedContainer = document.getElementById(button.dataset.localizedContainer);
        if (localizedContainer) {
          const localizedHTML = button.dataset.localizedFields.replace(/NEW_RECORD/g, time);
          const localizedWrapper = document.createElement('div');
          localizedWrapper.innerHTML = localizedHTML;
          localizedContainer.insertAdjacentElement('beforeend', localizedWrapper);
        }
      }

      event.preventDefault();
      });

  });
};

const setupRecurrenceRulesFormButtons = () => {
  $(document).ready(() => {
    $('form').on('click', '.js-remove-row', (event) => {
      const row = event.target.closest('tr');
      if (!row) return;

      $(row).find('input.js-remove-flag').val('1');
      $(row).hide();

      event.preventDefault();
    });

    $('form').on('click', '.js-all-day', (event) => {
      const row = event.target.closest('tr');
      const fromOffset = row.querySelector('.js-valid-from-offset');
      const untilOffset = row.querySelector('.js-valid-until-offset');

      fromOffset.tomselect.setValue('0');
      untilOffset.tomselect.setValue('86400');

      event.preventDefault();
    });

    $('form').on('click', '.js-copy', (event) => {
      document.querySelector('.js-add-row').click();

      const copiedRow = event.target.closest('tr');
      const copiedRowDayOfWeek = copiedRow.querySelector('.js-day-of-week').tomselect;
      const copiedRowFromOffset = copiedRow.querySelector('.js-valid-from-offset').tomselect;
      const copiedRowUntilOffset = copiedRow.querySelector('.js-valid-until-offset').tomselect;

      const lastRow = document.getElementById('recurrence_rulesTable').querySelector('tr:last-of-type');
      const lastRowDayOfWeek = lastRow.querySelector('.js-day-of-week').tomselect;
      const lastRowFromOffset = lastRow.querySelector('.js-valid-from-offset').tomselect;
      const lastRowUntilOffset = lastRow.querySelector('.js-valid-until-offset').tomselect;

      lastRowDayOfWeek.setValue(copiedRowDayOfWeek.getValue());
      lastRowFromOffset.setValue(copiedRowFromOffset.getValue());
      lastRowUntilOffset.setValue(copiedRowUntilOffset.getValue());

      event.preventDefault();
    });

    $('form').on('click', '.js-add-row', (event) => {
      const button = event.currentTarget;
      const tableId = button.dataset.table || 'recurrence_rulesTable';
      const table = document.getElementById(tableId);
      if (!table) return;

      const lastRow = table.querySelector('tr:last-of-type');

      const time = Date.now();
      const regexp = new RegExp(button.dataset.id, 'g');
      const newRowHTML = button.dataset.fields.replace(regexp, time);
      const newRow = document.createElement('tr');
      newRow.innerHTML = newRowHTML;

      const dayOfWeek = newRow.querySelector('.js-day-of-week');
      const validFromOffset = newRow.querySelector('.js-valid-from-offset');
      const validUntilOffset = newRow.querySelector('.js-valid-until-offset');

      if (dayOfWeek) setupTomSelect(dayOfWeek);
      if (validFromOffset) setupTomSelect(validFromOffset);
      if (validUntilOffset) setupTomSelect(validUntilOffset);

      lastRow.insertAdjacentElement('afterend', newRow);

      event.preventDefault();
    });

  });
};

export {
  setupTomSelect,
  setupTomSelectTagging,
  setupTomSelectAjax,
  setupRecurrenceRulesFormButtons,
  setupFareAdapterElementForm,
  initializeFareAdapterElementForm
};
